import React, { useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";

import api from "@/utils/api";
import { gtagEvent } from "@/utils/gtag";
import { InferValues } from "@/utils/hookForm";

import NutricoachLogo from "../../../../assets/Nutricoach.png";
import ForgotPasswordIcon from "../../../../components/Icons/ForgotPasswordIcon";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  image: {
    flexGrow: 1,
    textAlign: "center",
  },
  cardContainer: {
    width: "32vw",
  },
}));

const StyledForgotPasswordIcon = withStyles(
  ForgotPasswordIcon,
  (theme: Theme) => ({
    root: {
      height: 80,
      width: 64,
      margin: theme.spacing(2, 0),
    },
  })
);

const StyledTypography = withStyles(Typography, {
  root: {
    width: 300,
  },
});

const StyledButton = withStyles(Button, (theme: Theme) => ({
  root: {
    padding: theme.spacing(1, 3),
  },
}));

const StyledCardContent = withStyles(CardContent, {
  root: {
    padding: 40,
  },
});

const EmailInputPage: React.FC = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit({ email }: InferValues<typeof control>) {
    await api.post("coach_accounts/password", {
      coach_account: {
        email: email,
      },
    });
    setEmailSent(true);
    gtagEvent("forgot_password");
    reset();
  }
  return (
    <div className={classes.root}>
      <div className={classes.cardContainer}>
        <div className={classes.image}>
          <img src={NutricoachLogo} alt="Nutricoach Logo" />
        </div>
        <Card>
          <StyledCardContent>
            <StyledForgotPasswordIcon />
            <Typography variant="h6" fontWeight={800}>
              Forgot Password
            </Typography>
            <Box marginBottom={1} />
            <StyledTypography variant="caption" paragraph>
              Can{`'`}t remember your login details? That{`'`}s fine, it
              happens! Please enter your registered email and we will send you a
              reset password link.
            </StyledTypography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box height="80px" marginTop={theme.spacing(1)}>
                {emailSent ? (
                  <Box display="flex">
                    <CheckCircleIcon color="primary" />
                    <Box marginRight={theme.spacing(0.5)} />
                    <Typography color="primary">
                      A reset password link was sent to your email.
                    </Typography>
                  </Box>
                ) : (
                  <TextField
                    {...register("email")}
                    size="small"
                    type="email"
                    placeholder="Enter your email"
                    required
                    fullWidth
                  />
                )}
              </Box>
              <Box width="100%" textAlign="center">
                {!emailSent && (
                  <StyledButton type="submit" disabled={isSubmitting}>
                    Send
                  </StyledButton>
                )}
              </Box>
            </form>
          </StyledCardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailInputPage;
